/**
 * Global Intercom Trigger System
 *
 * Usage from anywhere in the app:
 *
 * // Basic trigger
 * window.dispatchEvent(new Event("openIntercom"));
 *
 * // Trigger with delay
 * window.dispatchEvent(new CustomEvent("openIntercom", {
 *   detail: { delay: 5000 }
 * }));
 *
 * // Trigger with custom options
 * window.dispatchEvent(new CustomEvent("openIntercom", {
 *   detail: {
 *     delay: 20000,
 *     reason: "login_success",
 *     excludePaths: ["/dashboard"]
 *   }
 * }));
 */

export enum IntercomTriggerReason {
  LOGIN_SUCCESS = "login_success",
  FAILED_LOGIN_ATTEMPTS = "failed_login_attempts",
  MANUAL = "manual",
  PAGE_LOAD = "page_load",
  USER_ACTION = "user_action",
}

export interface IntercomTriggerOptions {
  delay?: number; // delay in milliseconds
  reason?: IntercomTriggerReason | string;
  excludePaths?: string[]; // paths where intercom should not trigger
}

// Global trigger functions that can be called from anywhere
export const triggerIntercom = (options: IntercomTriggerOptions = {}) => {
  window.dispatchEvent(new CustomEvent("openIntercom", { detail: options }));
};

export const triggerIntercomOnLoginSuccess = (user?: any) => {
  triggerIntercom({
    delay: 210000, // 210 seconds
    reason: IntercomTriggerReason.LOGIN_SUCCESS,
    excludePaths: ["#/dashboard?patientIds"],
  });
};

export const triggerIntercomOnFailedLogin = (attemptCount: number) => {
  if (attemptCount >= 2) {
    triggerIntercom({
      delay: 1000, // 1 second
      reason: IntercomTriggerReason.FAILED_LOGIN_ATTEMPTS,
    });
  }
};

export const triggerIntercomOnPageLoad = (delayMs: number = 30000) => {
  triggerIntercom({
    delay: delayMs,
    reason: IntercomTriggerReason.PAGE_LOAD,
    excludePaths: ["#/dashboard?patientIds"],
  });
};

// Utility function to check if current path should be excluded
export const shouldExcludePath = (excludePaths: string[] = []) => {
  const currentPath = window.location.hash || window.location.pathname;
  return excludePaths.some((path) => currentPath.includes(path));
};
